import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Typography, Badge, Space, Tabs, Input, Select, DatePicker, Empty, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { SearchOutlined, CalendarOutlined, FileDoneOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getStudentExercises, getStudentExerciseDetail } from '../../api/exercises';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

const StudentExercises = () => {
  const navigate = useNavigate();
  const { exerciseId } = useParams();
  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [activeKey, setActiveKey] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [difficultyFilter, setDifficultyFilter] = useState('all');

  // 获取真实的学生练习数据
  useEffect(() => {
    const fetchExercises = async () => {
      try {
        setLoading(true);
        const result = await getStudentExercises();
        setExercises(result);
        setFilteredExercises(result);
      } catch (error) {
        console.error('获取练习列表失败:', error);
        message.error('获取练习列表失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    fetchExercises();
  }, []);

  // 根据服务器API进行高级筛选
  const fetchFilteredExercises = async () => {
    try {
      setLoading(true);
      
      // 构建筛选参数
      const params = {};
      
      // 添加状态筛选
      if (activeKey !== 'all') {
        params.status = activeKey;
      }
      
      // 添加难度筛选
      if (difficultyFilter !== 'all') {
        params.difficulty = difficultyFilter;
      }
      
      // 添加搜索文本
      if (searchText) {
        params.searchText = searchText;
      }
      
      // 添加日期筛选
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format('YYYY-MM-DD 00:00:00');
        params.endDate = dateRange[1].format('YYYY-MM-DD 23:59:59');
      }
      
      const result = await getStudentExercises(params);
      setFilteredExercises(result);
    } catch (error) {
      console.error('筛选练习失败:', error);
      message.error('筛选练习失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };
  
  // 应用筛选条件
  useEffect(() => {
    fetchFilteredExercises();
  }, [activeKey, difficultyFilter, searchText, dateRange]);

  // 处理搜索输入变化
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  // 处理日期范围变化
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  // 处理难度筛选变化
  const handleDifficultyChange = (value) => {
    setDifficultyFilter(value);
  };

  // 重置筛选条件
  const resetFilters = () => {
    setSearchText('');
    setDateRange(null);
    setDifficultyFilter('all');
    setActiveKey('all');
  };

  // 跳转到练习详情页面
  const goToExerciseDetail = (assignmentId) => {
    navigate(`/student/exercises/${assignmentId}`);
  };
  
  // 在新窗口打开测试页面
  const openExamPage = (exerciseId) => {
    const examUrl = `/exam/${exerciseId}`;
    window.open(examUrl, '_blank', 'noopener,noreferrer');
  };

  // 获取状态标签
  const getStatusTag = (status) => {
    switch (status) {
      case 'pending':
        return <Tag color="orange">待开始</Tag>;
      case 'in_progress':
        return <Tag color="blue">进行中</Tag>;
      case 'completed':
        return <Tag color="green">已完成</Tag>;
      case 'overdue':
        return <Tag color="red">已逾期</Tag>;
      default:
        return <Tag color="default">未知状态</Tag>;
    }
  };

  // 获取难度标签
  const getDifficultyTag = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return <Tag color="success">简单</Tag>;
      case 'medium':
        return <Tag color="processing">中等</Tag>;
      case 'hard':
        return <Tag color="warning">困难</Tag>;
      case 'advanced':
        return <Tag color="error">高级</Tag>;
      default:
        return <Tag color="default">未知</Tag>;
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '练习名称',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary">题目数: {record.question_count}</Text>
        </div>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category_name',
      key: 'category',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '难度',
      dataIndex: 'difficulty',
      key: 'difficulty',
      render: (difficulty) => getDifficultyTag(difficulty)
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      key: 'start_time',
      render: (text) => (
        <span>
          <CalendarOutlined style={{ marginRight: 8 }} />
          {dayjs(text).format('YYYY-MM-DD HH:mm')}
        </span>
      )
    },
    {
      title: '截止时间',
      dataIndex: 'end_time',
      key: 'end_time',
      render: (text) => (
        <span>
          <ClockCircleOutlined style={{ marginRight: 8 }} />
          {dayjs(text).format('YYYY-MM-DD HH:mm')}
        </span>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <div>
          {getStatusTag(status)}
          {status === 'in_progress' && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">已完成: {record.progress}%</Text>
            </div>
          )}
          {status === 'completed' && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">得分: {record.score}/{record.total_points}</Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        let buttonText = '查看详情';
        let buttonType = 'default';
        
        if (record.status === 'pending') {
          buttonText = '开始练习';
          buttonType = 'primary';
        } else if (record.status === 'in_progress') {
          buttonText = '继续练习';
          buttonType = 'primary';
        }
        
        return (
          <Button 
            type={buttonType} 
            onClick={() => {
              if (record.status === 'pending' || record.status === 'in_progress') {
                openExamPage(record.exercise_id);
              } else {
                goToExerciseDetail(record.exercise_id);
              }
            }}
          >
            {buttonText}
          </Button>
        );
      }
    }
  ];

  // 获取各种状态的练习数量
  const getStatusCount = (status) => {
    if (status === 'all') {
      return exercises.length;
    }
    return exercises.filter(e => e.status === status).length;
  };

  return (
    <div>
      <Card>
        <Title level={2} style={{ marginBottom: 24 }}>我的练习</Title>
        
        {/* 筛选区域 */}
        <div style={{ marginBottom: 24 }}>
          <Space size="large" style={{ marginBottom: 16 }}>
            <Input 
              placeholder="搜索练习名称或分类" 
              prefix={<SearchOutlined />} 
              allowClear
              value={searchText}
              onChange={handleSearchChange}
              style={{ width: 240 }}
            />
            
            <RangePicker 
              placeholder={['开始日期', '结束日期']}
              value={dateRange}
              onChange={handleDateRangeChange}
            />
            
            <Select
              placeholder="难度"
              style={{ width: 120 }}
              value={difficultyFilter}
              onChange={handleDifficultyChange}
            >
              <Option value="all">全部难度</Option>
              <Option value="easy">简单</Option>
              <Option value="medium">中等</Option>
              <Option value="hard">困难</Option>
              <Option value="advanced">高级</Option>
            </Select>
            
            <Button onClick={resetFilters}>重置筛选</Button>
          </Space>
        </div>
        
        {/* 标签页 */}
        <Tabs activeKey={activeKey} onChange={setActiveKey}>
          <TabPane 
            tab={
              <span>
                <Badge count={getStatusCount('all')} overflowCount={99} offset={[10, 0]}>
                  全部练习
                </Badge>
              </span>
            } 
            key="all"
          />
          <TabPane 
            tab={
              <span>
                <Badge 
                  count={getStatusCount('pending')} 
                  overflowCount={99} 
                  offset={[10, 0]}
                >
                  待开始
                </Badge>
              </span>
            } 
            key="pending"
          />
          <TabPane 
            tab={
              <span>
                <Badge 
                  count={getStatusCount('in_progress')} 
                  overflowCount={99} 
                  offset={[10, 0]}
                >
                  进行中
                </Badge>
              </span>
            } 
            key="in_progress"
          />
          <TabPane 
            tab={
              <span>
                <Badge 
                  count={getStatusCount('completed')} 
                  overflowCount={99} 
                  offset={[10, 0]}
                >
                  已完成
                </Badge>
              </span>
            } 
            key="completed"
          />
          <TabPane 
            tab={
              <span>
                <Badge 
                  count={getStatusCount('overdue')} 
                  overflowCount={99} 
                  offset={[10, 0]}
                >
                  已逾期
                </Badge>
              </span>
            } 
            key="overdue"
          />
        </Tabs>
        
        {/* 练习列表 */}
        <Table 
          columns={columns} 
          dataSource={filteredExercises} 
          rowKey="assignment_id" 
          loading={loading}
          locale={{
            emptyText: <Empty description="暂无练习数据" />
          }}
          pagination={{ 
            defaultPageSize: 5,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20']
          }}
        />
      </Card>
    </div>
  );
};

export default StudentExercises;  
